"use client";
import { Warning } from "@mui/icons-material";
import { Alert, Box, Button, Typography } from "@mui/material";
import dayjs from "dayjs";
import type React from "react";
import { useState } from "react";
import { mutate } from "swr";
import { SWR_KEYS } from "@/config/constants";
import { useAuth } from "@/features/auth";
import {
	TermsConfirmDialog,
	TermsPreviewDialog,
	TermsUploadCard,
	VersionHistorySection,
} from "@/features/terms";
import { SnackbarNotification } from "@/shared";
import { useS3Operations, useTerms } from "@/shared/hooks";
import type { TermType, TermVersion } from "@/shared/types";

interface TermsFile {
	file: File | null;
	content: string;
}

export interface TermsConfig {
	type: TermType;
	pageTitle: string;
	termTitle: string;
}

interface TermsManagementPageProps {
	config: TermsConfig;
}

export function TermsManagementPage({ config }: TermsManagementPageProps): React.ReactElement {
	const { type, pageTitle, termTitle } = config;
	const { usergroup } = useAuth();
	const { uploadTermHtml, getTermVersionHistory } = useS3Operations();
	const { registerTermVersion } = useTerms();

	const [terms, setTerms] = useState<TermsFile>({
		file: null,
		content: "",
	});

	const [isLoading, setIsLoading] = useState(false);
	const [message, setMessage] = useState("");
	const [alertType, setAlertType] = useState<"success" | "error">("success");
	const [openSnackbar, setOpenSnackbar] = useState(false);
	const [confirmModalOpen, setConfirmModalOpen] = useState(false);
	const [modalOpen, setModalOpen] = useState(false);
	const [modalContent, setModalContent] = useState("");
	const [modalTitle, setModalTitle] = useState("");

	const validateHtmlFile = async (file: File): Promise<boolean> => {
		const text = await file.text();
		// 危険なタグ/属性をチェック
		const blacklist = [/script/gi, /onerror\s*=/gi, /javascript:/gi, /<iframe/gi];
		return !blacklist.some((regex) => regex.test(text));
	};

	const handleFileUpload = async (file: File) => {
		if (!file.name.endsWith(".html")) {
			setMessage("HTML(.html)ファイルのみアップロード可能です");
			setAlertType("error");
			setOpenSnackbar(true);
			return;
		}

		const isValid = await validateHtmlFile(file);
		if (!isValid) {
			setMessage("不正なHTMLタグが検出されました");
			setAlertType("error");
			setOpenSnackbar(true);
			return;
		}

		// FileReaderを使用してHTMLファイルを完全にそのまま読み込み、一切の加工を行わない
		const reader = new FileReader();
		reader.onload = () => {
			const htmlContent = reader.result as string;
			setTerms({
				file,
				content: htmlContent,
			});
			// ファイルアップロード完了
			setMessage("ファイルが正常にアップロードされました");
			setAlertType("success");
			setOpenSnackbar(true);
		};
		reader.onerror = () => {
			setMessage("ファイルの読み込み中にエラーが発生しました");
			setAlertType("error");
			setOpenSnackbar(true);
		};
		reader.readAsText(file, "UTF-8");
	};

	const isTermsReady = () => {
		return terms.content.trim() !== "";
	};

	const isValidHTML = (html: string) => {
		if (!html.trim()) return false;
		return html.includes("<") && html.includes(">");
	};

	const handleConfirmSave = () => {
		setConfirmModalOpen(true);
	};

	const handleCancelConfirm = () => {
		setConfirmModalOpen(false);
	};

	const handleSave = async () => {
		if (!isTermsReady()) return;

		setConfirmModalOpen(false);
		setIsLoading(true);
		try {
			if (terms.content) {
				// 現在のバージョン一覧を取得して次のバージョン番号を決定
				let versions: TermVersion[] = [];
				try {
					versions = await getTermVersionHistory(type);
				} catch {
					// terms.jsonが存在しない場合は空配列
				}

				// 既存バージョンの最大値を取得（配列の長さではなく実際のバージョン番号から）
				const maxVersionNumber = versions.reduce((max, v) => {
					const versionNum = Number.parseInt(v.version.replace(/^v/, ""), 10);
					return versionNum > max ? versionNum : max;
				}, 0);

				const nextVersionNumber = maxVersionNumber + 1;
				const newVersionString = `v${nextVersionNumber}`;

				// HTMLをS3にアップロード
				const { s3Uri } = await uploadTermHtml(type, newVersionString, terms.content);

				// terms.jsonを更新 (API経由)
				const newVersion: TermVersion = {
					version: newVersionString,
					file: s3Uri,
					date: dayjs().format("YYYY/MM/DD HH:mm:ss"),
					title: termTitle,
				};
				await registerTermVersion(type, newVersion);

				setMessage("利用規約の発行が完了しました");
				setAlertType("success");
				setOpenSnackbar(true);

				setTerms({
					file: null,
					content: "",
				});

				// バージョン履歴を再取得
				mutate(SWR_KEYS.termVersionHistory(type));
			}
		} catch (_error) {
			setMessage("利用規約の発行に失敗しました");
			setAlertType("error");
			setOpenSnackbar(true);
		} finally {
			setIsLoading(false);
		}
	};

	const handleCancel = () => {
		setTerms({
			file: null,
			content: "",
		});
	};

	const fetchTermsContent = (version: string, htmlContent: string) => {
		setModalContent(htmlContent);
		setModalTitle(`${version} - ${termTitle}`);
		setModalOpen(true);
	};

	return (
		<Box sx={{ m: 2 }}>
			<Box sx={{ ml: 4, display: "flex", flexDirection: "column" }}>
				<Box sx={{ mb: 8 }}>
					<Typography component="h2" variant="h6" sx={{ fontWeight: "bold", mb: 2 }}>
						{pageTitle}
					</Typography>

					{/* バージョン履歴セクション */}
					<VersionHistorySection
						type={type}
						onContentFetch={fetchTermsContent}
						onError={(errorMessage) => {
							setMessage(errorMessage);
							setAlertType("error");
							setOpenSnackbar(true);
						}}
					/>

					{usergroup === "管理者" && (
						<Box>
							<Box sx={{ width: "100%" }}>
								<TermsUploadCard
									title="利用規約"
									fileName={terms.file?.name ?? null}
									content={terms.content}
									isLoading={isLoading}
									onFileSelect={handleFileUpload}
								/>
							</Box>

							<Box sx={{ mt: 4, display: "flex", flexDirection: "column", gap: 2 }}>
								{terms.content && !isValidHTML(terms.content) && (
									<Alert severity="error" icon={<Warning />}>
										HTMLが無効です。正しいHTML形式で入力してください。
									</Alert>
								)}

								<Box sx={{ display: "flex", justifyContent: "center", gap: 8 }}>
									<Button
										variant="contained"
										color="secondary"
										onClick={handleCancel}
										disabled={isLoading}
										sx={{ width: 170 }}
									>
										キャンセル
									</Button>
									<Button
										variant="contained"
										color="primary"
										onClick={handleConfirmSave}
										disabled={
											!isTermsReady() ||
											Boolean(terms.content && !isValidHTML(terms.content)) ||
											isLoading
										}
										sx={{ width: 170 }}
									>
										利用規約を更新する
									</Button>
								</Box>
							</Box>
						</Box>
					)}
				</Box>
			</Box>

			<SnackbarNotification
				open={openSnackbar}
				onClose={() => setOpenSnackbar(false)}
				message={message}
				severity={alertType}
			/>

			{/* 利用規約更新確認モーダル */}
			<TermsConfirmDialog
				open={confirmModalOpen}
				onClose={handleCancelConfirm}
				onConfirm={handleSave}
				isLoading={isLoading}
			/>

			{/* 利用規約表示モーダル */}
			<TermsPreviewDialog
				open={modalOpen}
				onClose={() => setModalOpen(false)}
				title={modalTitle}
				content={modalContent}
				isLoading={false}
			/>
		</Box>
	);
}
